import { useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../api/client';
import { API_ITEMS_URL } from '../utils';
import { lookupSearchQuery } from '../utils/lookupSearch';
import { markReadable } from '../utils/linkedRecords';

export const LOOKUP_SEARCH_DEBOUNCE_MS = 250;

/**
 * X-Total-Count, the rule `readTotalCount` (api/client.js) states: the
 * header in any case, else the fallback. Restated rather than imported
 * because the card-lookup input renders inside dozens of test suites that
 * mock the client module with `api` alone — an import from it would be
 * `undefined` there and every lookup would silently list nothing.
 */
export function lookupTotalOf(headers, fallback) {
	if (!headers) return fallback;
	const key = Object.keys(headers).find(name => name.toLowerCase() === 'x-total-count');
	const parsed = key === undefined ? NaN : Number(headers[key]);
	return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

/**
 * One page of a lookup's target, through the list endpoint, in the scope the
 * picker's first page is fetched in: the source collection, the target card
 * and the cards beneath it (`include_descendants`), and `q` — the picker
 * filter, a typed search, or both. `stored` reads the query as stored text
 * (`q_stored`), as the picker filter always is (keepr-api
 * utils/lookupFilter.js); a search ANDed onto it only narrows further.
 */
export async function fetchLookupPage({ collectionId, cardId, q = '', stored = false, limit, skip = 0, signal } = {}) {
	const params = { collection_id: collectionId, card_id: cardId, include_descendants: true };
	if (q) params.q = q;
	if (q && stored) params.q_stored = true;
	if (limit) params.limit = limit;
	if (skip) params.skip = skip;
	const response = await api.get(API_ITEMS_URL, { params, signal });
	const rows = Array.isArray(response?.data) ? response.data : [];
	markReadable(rows.map(row => row?._id));
	return { rows, total: lookupTotalOf(response?.headers, rows.length) };
}

const IDLE = { term: '', active: false, loading: false, error: false, rows: [], total: 0 };

/**
 * useLookupSearch — a card lookup's server search (KPR-466; utils/lookupSearch.js
 * says why it is the server's, and what "the name" is).
 *
 *   searchText     what the person has typed, as typed
 *   fields         lookupSearchFields(...) — empty means "cannot search here"
 *   filter         the picker filter when it narrows, else ''
 *   listWhenEmpty  with no term, list the scope anyway (the View all modal);
 *                  without it, no term means no request (the dropdown keeps
 *                  its own first page, unchanged)
 *   skip, limit    the page
 *   enabled        false asks nothing (a closed modal, a lookup not set up)
 *
 * Returns { term, active, loading, error, rows, total, settled, pending }:
 *   term     the debounced term the rows answer
 *   active   a server answer is (or will be) what the caller shows; false
 *            means fall back to the browser's filter over the first page —
 *            no term, or no name element to search
 *   settled  the rows answer what is typed now (not a keystroke behind)
 *   pending  what is typed now will be asked, and its answer is not in yet
 *
 * Typing is debounced so a word is one request, not one per letter. Every
 * answer carries the request it answers: a slow reply to "l" landing after
 * the reply to "lisinopril" is dropped, never shown, and the request it
 * belongs to is aborted when the next one starts.
 */
export default function useLookupSearch({
	collectionId,
	cardId,
	searchText = '',
	fields,
	filter = '',
	listWhenEmpty = false,
	skip = 0,
	limit = 50,
	enabled = true,
	delay = LOOKUP_SEARCH_DEBOUNCE_MS,
} = {}) {
	const typed = String(searchText == null ? '' : searchText).trim();
	const [term, setTerm] = useState(typed);
	// Opened on a term (the View all modal, seeded with what was typed in the
	// picker): that term is asked at once. Debouncing it would first list the
	// whole card for 250 ms — the wrong records, then the right ones.
	const [wasEnabled, setWasEnabled] = useState(enabled);
	if (enabled !== wasEnabled) {
		setWasEnabled(enabled);
		if (enabled && term !== typed) setTerm(typed);
	}
	useEffect(() => {
		// Clearing the box answers at once: the first page is already in hand.
		if (!typed) { setTerm(''); return undefined; }
		const handle = setTimeout(() => setTerm(typed), delay);
		return () => clearTimeout(handle);
	}, [typed, delay]);

	const query = useMemo(() => {
		if (!term) return listWhenEmpty ? filter : '';
		return lookupSearchQuery({ fields, term, filter });
	}, [term, fields, filter, listWhenEmpty]);
	// Something to ask: a term the fields can express, or (listing) the scope.
	const askable = Boolean(enabled && collectionId && cardId) && (term ? Boolean(query) : listWhenEmpty);

	const [state, setState] = useState(IDLE);
	const requestRef = useRef(0);
	useEffect(() => {
		if (!askable) {
			requestRef.current += 1;
			setState(IDLE);
			return undefined;
		}
		const token = ++requestRef.current;
		const controller = typeof AbortController === 'function' ? new AbortController() : null;
		setState(current => ({ ...current, term, active: true, loading: true, error: false }));
		fetchLookupPage({ collectionId, cardId, q: query, stored: Boolean(filter), limit, skip, signal: controller?.signal })
			.then(({ rows, total }) => {
				if (token !== requestRef.current) return;
				setState({ term, active: true, loading: false, error: false, rows, total });
			})
			.catch(() => {
				if (token !== requestRef.current) return;
				setState({ term, active: true, loading: false, error: true, rows: [], total: 0 });
			});
		return () => { controller?.abort(); };
	}, [askable, collectionId, cardId, query, filter, limit, skip, term]);

	// A term typed that the debounce has not sent yet, or that is out: the
	// caller says "Searching…" rather than letting an empty list read as
	// "nothing here" for those 250 ms. Only when the term CAN be asked — a
	// term the fields cannot express never resolves, and must not spin.
	const typedAskable = useMemo(
		() => Boolean(typed) && Boolean(enabled && collectionId && cardId) && Boolean(lookupSearchQuery({ fields, term: typed, filter })),
		[typed, enabled, collectionId, cardId, fields, filter],
	);
	const settled = !state.loading && state.term === typed;
	return { ...state, settled, pending: typedAskable && !settled };
}
